import { useColorMode } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import type { AvailableLocales } from '@/libs/i18n'

// Where the appearance choice is kept. This is VueUse's own default key and is
// deliberately not renamed: the mechanism already exists in the app before this
// store does — Nuxt UI's vue plugin installs VueUse's useDark() when it is
// registered (main.ts), and THAT is what puts the `dark` class on <html>, writes
// the choice here, and follows the OS while the choice is "auto". This store
// attaches to the same state instead of starting a rival one.
//
// The inline script in index.html reads this key too, to get the class on <html>
// before the first paint. Both places or neither.
const COLOR_MODE_STORAGE_KEY = 'vueuse-color-scheme'

// Jackdaw auth is the BFF model (tech spec §8.4): the server holds the session
// behind an HttpOnly cookie; the SPA holds only a short-lived bearer token in
// memory, refreshed from GET /auth/session. Nothing auth-related touches
// localStorage and the SPA never writes cookies — this deliberately replaces
// the house JWT-in-localStorage pattern.
export const useAppStore = defineStore('app', () => {
    const sessionToken   = ref('')
    const tokenExpiresAt = ref<Date>()
    // English is the only locale for now (tech spec §12); the switch stays so a
    // second catalogue can be added without touching call sites.
    const locale         = ref<AvailableLocales>('en')
    const windowWidth    = ref(window.innerWidth)
    const loaderActive   = ref(0)

    // Appearance (func spec §25 — Light and Dark; the house theme has no System
    // option, owner decision 2026-09-23). Wraps the SAME state Nuxt UI's vue
    // plugin already manages via useDark(), never a second source of truth.
    //
    // No `emitAuto`, so this ref always reads the RESOLVED mode, 'light' or
    // 'dark': on a first visit — nothing stored yet — that is whatever the
    // device prefers, and the first write persists an explicit choice under the
    // same key the pre-paint script in index.html reads. VueUse's default class
    // vocabulary (`light` / `dark` on <html>) is kept, which is the one the
    // theme's `:root` / `.dark` scoping and that script both speak.
    const colorMode = useColorMode({ storageKey: COLOR_MODE_STORAGE_KEY })

    const getSessionToken = () => sessionToken.value
    const isLoaderActive  = () => loaderActive.value > 0
    const isMobile        = () => windowWidth.value < 1024

    const tokenIsFresh = () =>
        !!sessionToken.value && !!tokenExpiresAt.value && tokenExpiresAt.value.getTime() - Date.now() > 60_000

    const setSession = (token: string, expiresAt: string) => {
        sessionToken.value = token
        tokenExpiresAt.value = new Date(expiresAt)
    }

    const clearSession = () => {
        sessionToken.value = ''
        tokenExpiresAt.value = undefined
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: AvailableLocales) => {
        locale.value = newLocale
        ;(i18n.global.locale as { value: string }).value = newLocale
    }

    return {
        locale, loaderActive, colorMode,
        getSessionToken, isLoaderActive, isMobile, tokenIsFresh,
        setSession, clearSession, startLoading, stopLoading, changeLocale,
    }
})
